import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useFeaturesReady } from "../featuresReady/FeaturesReadyContext";
import { getFeature } from "../../utils/features";
import { useProduct } from "../product/ProductContext";
import * as mySizeProfileApi from "../../api/mySizeProfileApi";
import type { MySizeProfile } from "../../api/mySizeProfileApiTypes";
import { getCustomerIsAuthenticated } from "../../utils/identity";
import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";
import {
  getMySizeProfileDismissed,
  setMySizeProfileDismissed,
} from "../../utils/mySizeProfileStorage";

interface MySizeProfileContextType {
  mySizeProfile: MySizeProfile | null;
  loading: boolean;
  error: Error | null;
  dismissed: boolean;
  isEligible: boolean;
  isAuthenticated: boolean;
  isMySizeProfileEnabled: boolean;
  saveMySizeProfile: (
    sizeProfileId: string,
    legLength: number | null
  ) => Promise<MySizeProfile>;
  dismissMySizeProfile: () => void;
}

const MySizeProfileContext = createContext<
  MySizeProfileContextType | undefined
>(undefined);

interface MySizeProfileContextProviderProps {
  children: ReactNode;
}

export const MySizeProfileContextProvider = ({
  children,
}: MySizeProfileContextProviderProps) => {
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };
  const { product } = useProduct();

  const [mySizeProfile, setMySizeProfile] = useState<MySizeProfile | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() =>
    getMySizeProfileDismissed()
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const isMySizeProfileEnabled =
    areFeaturesReady &&
    (getFeature("pdp_my_size_profile")?.isEnabled() ?? false);
  const gender = product?.gender?.toLowerCase() ?? "";
  const productTypeName = product?.productType?.name?.toLowerCase() ?? "";
  const isEligible =
    isMySizeProfileEnabled &&
    !!product &&
    gender.includes("women") &&
    productTypeName.includes("jeans");

  useEffect(() => {
    if (!isEligible) {
      setMySizeProfile(null);
      setLoading(false);
      setError(null);
      return;
    }

    let isCancelled = false;

    const fetch = async () => {
      if (isCancelled) return;

      setLoading(true);
      setError(null);

      try {
        const isLoggedIn = await getCustomerIsAuthenticated();
        if (isCancelled) return;

        if (!isLoggedIn) {
          setIsAuthenticated(false);
          setMySizeProfile(null);
          return;
        }
        setIsAuthenticated(true);

        const data = await mySizeProfileApi.fetchMySizeProfile();
        if (isCancelled) return;

        setMySizeProfile(data);
      } catch (err) {
        if (isCancelled) return;

        setError(err as Error);
        setMySizeProfile(null);
        logError(ERROR_TYPE.UserActionFailed, {
          action: "fetchMySizeProfile",
          caller: "MySizeProfileContext",
          apiName: "MySizeProfile",
          message: (err as Error).message,
        });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetch();

    return () => {
      isCancelled = true;
    };
  }, [isEligible, product?.id]);

  const saveSizeProfile = async (
    sizeProfileId: string,
    legLength: number | null
  ): Promise<MySizeProfile> => {
    setLoading(true);
    setError(null);

    try {
      const data = await mySizeProfileApi.saveMySizeProfile(
        sizeProfileId,
        legLength
      );
      setMySizeProfile(data);
      return data;
    } catch (err) {
      setError(err as Error);
      logError(ERROR_TYPE.UserActionFailed, {
        action: "saveMySizeProfile",
        caller: "MySizeProfileContext",
        apiName: "MySizeProfile",
        message: (err as Error).message,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const saveMySizeProfile: MySizeProfileContextType["saveMySizeProfile"] =
    saveSizeProfile;

  const dismissMySizeProfile = (): void => {
    setDismissed(true);
    setMySizeProfileDismissed(true);
  };

  const value: MySizeProfileContextType = {
    mySizeProfile,
    loading,
    error,
    dismissed,
    isEligible,
    isAuthenticated,
    isMySizeProfileEnabled,
    saveMySizeProfile,
    dismissMySizeProfile,
  };

  return (
    <MySizeProfileContext.Provider value={value}>
      {children}
    </MySizeProfileContext.Provider>
  );
};

export const useMySizeProfile = (): MySizeProfileContextType => {
  const context = useContext(MySizeProfileContext);
  if (!context) {
    throw new Error(
      "useMySizeProfile must be used within MySizeProfileContextProvider"
    );
  }
  return context;
};

export const useOptionalMySizeProfile = () => useContext(MySizeProfileContext);
